<template>
  <BottomDrawer
    :is-shown="state.isShown"
    @close="close"
  >
    <div class="content">
      <template v-if="payment.status === paymentStatuses.success">
        <div class="successIcon">
          <div class="gradient success"/>
        </div>

        <Typography
          variant="v20-700"
          center
          style="margin-top: 8px"
        >
          Оплачено
        </Typography>

        <Typography
          v-if="subscriptionSummary"
          variant="v14-500"
          center
        >
          {{ subscriptionSummary }}
        </Typography>

        <Typography
          variant="v14-400"
          color="secondary"
          center
        >
          Чек отправлен на электронную почту {{ store.state.user.profile.email }}
        </Typography>
      </template>

      <template v-if="payment.status === paymentStatuses.pending">
        <div class="pendingIcon">
          <div class="gradient pending"/>
        </div>

        <Typography
          variant="v20-700"
          center
          style="margin-top: 8px"
        >
          {{ pendingTitle }}
        </Typography>

        <Typography
          v-if="subscriptionSummary"
          variant="v14-500"
          center
        >
          {{ subscriptionSummary }}
        </Typography>

        <template v-if="isExternalPayment">
          <Typography
            variant="v14-400"
            color="secondary"
            center
          >
            {{ state.needsTap
              ? 'Нажми «Открыть оплату» — страница откроется в браузере.'
              : 'Оплати там и вернись в Telegram. Статус обновится сам.' }}
          </Typography>
        </template>
        <template v-else>
          <Typography
            variant="v14-400"
            color="secondary"
            center
          >
            Это может занять некоторое время
          </Typography>
          <Typography
            variant="v14-400"
            color="secondary"
            center
          >
            После обработки платежа чек будет отправлен на электронную почту {{ store.state.user.profile.email }}
          </Typography>
        </template>
      </template>

      <template v-if="payment.status === paymentStatuses.failed">
        <div class="failedIcon">
          <div class="gradient failed"/>
        </div>

        <Typography
          variant="v20-700"
          center
          style="margin-top: 8px"
        >
          Ошибка при обработке платежа
        </Typography>

        <Typography
          v-if="subscriptionSummary"
          variant="v14-500"
          center
        >
          {{ subscriptionSummary }}
        </Typography>

        <Typography
          variant="v14-400"
          color="secondary"
          center
        >
          Убедись что на карте достаточно средств и попробуй ещё раз
        </Typography>
      </template>
    </div>

    <div class="modal-divider"/>

    <AppButton
      v-if="payment.status === paymentStatuses.success && tgChatLink"
      full-width
      style="margin: 16px 0"
      @click="handleButtonClick"
    >
      Перейти в ТГ чат подписки
    </AppButton>

    <AppButton
      v-if="payment.status === paymentStatuses.pending && isExternalPayment"
      full-width
      style="margin: 16px 0"
      @click="reopenExternalPayment"
    >
      Открыть оплату
    </AppButton>

    <AppButton
      v-if="payment.status === paymentStatuses.pending"
      full-width
      style="margin: 16px 0"
      :loading="state.subscriptionLoading"
      :disabled="state.subscriptionLoading"
      :variant="isExternalPayment ? 'secondary' : undefined"
      @click="checkSubscriptionStatus"
    >
      {{ isExternalPayment ? 'Проверить оплату' : 'Проверить статус платежа' }}
    </AppButton>

    <AppButton
      v-if="payment.status === paymentStatuses.pending && isExternalPayment"
      full-width
      style="margin: 16px 0"
      variant="secondary"
      @click="chooseAnotherTariff"
    >
      Выбрать другой тариф
    </AppButton>

    <AppButton
      v-if="payment.status === paymentStatuses.failed"
      full-width
      style="margin: 16px 0"
      @click="handleFailedButtonClick"
    >
      Попробовать ещё раз
    </AppButton>

    <AppButton
      v-if="!isExternalPayment || payment.status !== paymentStatuses.pending"
      full-width
      style="margin: 16px 0"
      variant="secondary"
      @click="close"
    >
      Закрыть
    </AppButton>
  </BottomDrawer>
</template>

<script setup>
import {computed, onBeforeMount, reactive, watch} from 'vue'

import { appAxios } from '@/axios.js'
import { paymentStatuses } from '@/configs/subscriptionStatuses.js'
import { router } from '@/router/router.js'
import store from '@/store/store.js'
import {
  getOnboardingFinishRouteName,
  resolveTariffSelectionRouteName,
  tryFinishBodybaseOnboardingOnActiveSubscription,
} from '@/utils/onboardingSubscription.js'
import {
  clearPendingExternalPayment,
  getPendingExternalPayment,
  reopenPendingExternalPayment,
} from '@/utils/openPaymentUrl.js'

import AppButton from '@/components/UI/AppButton.vue'
import BottomDrawer from '@/components/BottomDrawer.vue'
import Typography from '@/components/UI/Typography.vue'

const SUBSCRIPTION_CONTROL_ROUTE = 'SubscriptionControl'
const rubFormat = new Intl.NumberFormat('ru-RU', {})

const props = defineProps({
  data: {
    type: Object,
    default: {},
  }
})

const isBodybaseProject = computed(() => store.getters['app/isBodybaseProject'])

const isExternalPayment = computed(() => (
  Boolean(props.data?.external || store.state.app.messageComponentData?.external)
))

const paymentId = computed(() =>
  props.data?.query?.payment_id
  || store.state.app.messageComponentData?.query?.payment_id
  || getPendingExternalPayment()?.paymentId,
)

const state = reactive({
  isShown: false,
  subscriptionLoading: false,
  // Бек ответил позже окна openLink — страницу оплаты открывает только кнопка
  needsTap: Boolean(props.data?.needsTap ?? store.state.app.messageComponentData?.needsTap),
})

const pendingTitle = computed(() => {
  if (!isExternalPayment.value) {
    return 'Ожидаем подтверждение платежа'
  }
  return state.needsTap ? 'Оплата готова' : 'Оплата открылась в браузере'
})

const userProfile = computed(() => {
  return store.state.user?.profile
})
const subscriptions = computed(() => {
  return userProfile.value?.subscriptions || []
})

const matchedPaymentUpdate = computed(() => {
  const id = paymentId.value
  const paymentUpdate = store.state.user.paymentUpdate

  if (
    paymentUpdate?.payment != null
    && id != null
    && String(paymentUpdate.payment.id) === String(id)
  ) {
    return paymentUpdate
  }

  return null
})

const paymentMatch = computed(() => {
  const id = paymentId.value
  if (id == null) {
    return null
  }

  for (const subscription of subscriptions.value) {
    const found = subscription.payments?.find(p => String(p.id) === String(id))
    if (found) {
      return { payment: found, subscription }
    }
  }

  return null
})

/** Сначала WS payment_update по payment_id, иначе платёж из profile.subscriptions */
const payment = computed(() => {
  return matchedPaymentUpdate.value?.payment
    ?? paymentMatch.value?.payment
    ?? { status: paymentStatuses.pending }
})

function resolveSubscriptionPriceAmount(subscription) {
  return Number(
    subscription?.price?.final_price
    ?? subscription?.price?.discounted_price
    ?? subscription?.price?.price,
  )
}

/** Bodybase: подписка для платежа — сначала subscription_id, иначе amount_requested */
const paymentSubscription = computed(() => {
  if (!isBodybaseProject.value) {
    return null
  }

  const list = subscriptions.value
  if (!list.length) {
    return null
  }

  const pay = payment.value
  if (pay?.subscription_id != null) {
    const byId = list.find(sub => String(sub.id) === String(pay.subscription_id))
    if (byId) {
      return byId
    }
  }

  const requestedAmount = Number(pay?.amount_requested)
  if (Number.isFinite(requestedAmount)) {
    const byAmount = list.find(sub => resolveSubscriptionPriceAmount(sub) === requestedAmount)
    if (byAmount) {
      return byAmount
    }
  }

  return paymentMatch.value?.subscription ?? null
})

const subscriptionSummary = computed(() => {
  const price = paymentSubscription.value?.price
  if (!price) {
    return null
  }

  const amount = resolveSubscriptionPriceAmount(paymentSubscription.value)
  const amountLabel = Number.isFinite(amount) ? `${rubFormat.format(amount)} ₽` : null

  if (price.title && amountLabel) {
    return `${price.title} · ${amountLabel}`
  }

  return price.title || amountLabel
})

const tgChats = computed(() => {
  if (matchedPaymentUpdate.value) {
    return Array.isArray(matchedPaymentUpdate.value.tg_chats)
      ? matchedPaymentUpdate.value.tg_chats
      : []
  }

  const fromSubscription = (
    paymentSubscription.value?.tg_chats
    ?? paymentMatch.value?.subscription?.tg_chats
  )
  return Array.isArray(fromSubscription) ? fromSubscription : []
})

const tgChatLink = computed(() => tgChats.value[0]?.tg_link || null)

watch(() => subscriptions.value, () => {
  setTimeout(() => {
    state.isShown = true
  }, 50)
}, { deep: true, immediate: true })

watch(
  () => payment.value?.status,
  (status) => {
    if (
      status === paymentStatuses.success
      || status === paymentStatuses.failed
    ) {
      clearPendingExternalPayment()
    }
  },
)

async function getUserSubscriptions() {
  const res = await appAxios.subscription.userSubscriptions(userProfile.value.id)

  await store.commit('user/setProfile', {
    subscriptions: res.data.subscriptions,
  })
}

async function close() {
  state.isShown = false
  store.dispatch('app/setMessageComponent', null)

  // Bodybase: бэку «онбординг закончен» — только после первой active (PUT последних шагов)
  const finished = await tryFinishBodybaseOnboardingOnActiveSubscription()
  if (finished) {
    await router.push({ name: getOnboardingFinishRouteName() })
  }
}

async function checkSubscriptionStatus() {
  if (state.subscriptionLoading) return

  state.subscriptionLoading = true
  try {
    await getUserSubscriptions()
  } finally {
    setTimeout(() => {
      state.subscriptionLoading = false
    }, 50)
  }
}

async function reopenExternalPayment() {
  if (!(await reopenPendingExternalPayment())) {
    // Нет url в storage — пользователь может заново выбрать тариф
    chooseAnotherTariff()
    return
  }
  state.needsTap = false
}

function chooseAnotherTariff() {
  clearPendingExternalPayment()
  state.isShown = false
  store.dispatch('app/setMessageComponent', null)
  router.push({ name: resolveTariffSelectionRouteName() })
}

function handleFailedButtonClick() {
  state.isShown = false
  store.dispatch('app/setMessageComponent', null)
  router.push({ name: SUBSCRIPTION_CONTROL_ROUTE })
}

function handleButtonClick() {
  if (!tgChatLink.value) return
  window.open(tgChatLink.value, '_blank')
}

onBeforeMount(() => {
  getUserSubscriptions()
})
</script>

<style lang="scss" scoped>

.modal-divider {
  width: 100%;
  height: 1px;
  background: var(--app-bar-border-color);
  margin: 16px 0;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  .gradient {
    position: absolute;
    width: 224px;
    height: 224px;
    left: -50%;
    top: -50%;

    &.success {
      background: radial-gradient(rgba(86, 231, 118, 0.35) 0%, rgba(88, 234, 120, 0.05) 25%, rgba(0, 0, 0, 0) 50%);
    }

    &.pending {
      background: radial-gradient(rgb(255, 153, 0, 0.35) 0%, rgba(255, 153, 0, 0.06) 25%, rgba(0, 0, 0, 0) 50%);
    }

    &.failed {
      background: radial-gradient(rgb(255, 48, 58, 0.35) 0%, rgba(255, 48, 58, 0.06) 25%, rgba(0, 0, 0, 0) 50%);
    }
  }

  .successIcon {
    position: relative;
    background-image: url("@/assets/icons/bigSuccess.svg");
    height: 112px;
    width: 112px;
  }

  .pendingIcon {
    position: relative;
    background-image: url("@/assets/icons/bigPending.svg");
    height: 112px;
    width: 112px;
  }

  .failedIcon {
    position: relative;
    background-image: url("@/assets/icons/bigFailed.svg");
    height: 112px;
    width: 112px;
  }
}
</style>
